// Classification of a gift-card balance-check failure.
//
// Extracted from GiftCardSellModal so the decision is testable without a
// DOM. The distinction matters for money: "not found" means two opposite
// things depending on which gift-card system the store runs, and treating
// an inconclusive failure as either one sells a card nobody verified.
//
// The v2 endpoint emits exactly three codes:
//   gift_card_not_found       + can_create: true   Barnet store, unseen
//                                                  number -> create fresh
//   gift_card_not_at_provider + can_create: false  Birchmount store,
//                                                  Birchmount does not hold
//                                                  it -> unsellable
//   birchmount_unavailable                         provider down, retry
// Anything else -- network, timeout, 500, HTML interstitial, the
// clerk-reauth pause -- tells us NOTHING about the card.

export type BalanceCheckVerdict =
  /** Provider is down. Retryable; says nothing about the card. */
  | 'unavailable'
  /** The provider does not hold this number. It cannot be sold. */
  | 'not-at-provider'
  /** Confirmed absent from our own system: the legitimate new-card path. */
  | 'new-card'
  /** The check did not complete. We know nothing; do not let a sale proceed. */
  | 'inconclusive'

export interface BalanceCheckError {
  // V2ApiError types this as string | null, so null must be representable
  // here or the cast at the call site quietly erases it.
  code?: string | null
  extra?: { can_create?: boolean }
}

/**
 * Decide what a failed balance check tells us about the card.
 *
 * Two asymmetries are deliberate:
 *
 *  - The blocking verdict is tested FIRST, so a contradictory response
 *    (a not_found code carrying can_create:false) refuses rather than sells.
 *  - `can_create` alone is enough to BLOCK but not to PERMIT. The backend
 *    always sends the flag together with its code, so nothing needs the
 *    permissive flag-alone path — and leaving it in would be the one rule
 *    that could turn an unrecognised error shape into a sale.
 */
export function classifyBalanceCheckError(err: BalanceCheckError | null | undefined): BalanceCheckVerdict {
  const code = err?.code
  const canCreate = err?.extra?.can_create

  if (code === 'birchmount_unavailable') return 'unavailable'
  if (code === 'gift_card_not_at_provider' || canCreate === false) return 'not-at-provider'
  if (code === 'gift_card_not_found') return 'new-card'
  return 'inconclusive'
}
